import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Languages, Mic, Camera, Volume2, ArrowRightLeft, Play } from 'lucide-react';

const TranslatorDemo = () => {
  const [activeDemo, setActiveDemo] = useState('translation');
  const [isPlaying, setIsPlaying] = useState(false);

  const demoTypes = [
    {
      id: 'translation',
      label: 'Smart Translation',
      icon: Languages,
      color: 'blue'
    },
    {
      id: 'voice',
      label: 'Voice Input',
      icon: Mic,
      color: 'purple'
    },
    {
      id: 'camera',
      label: 'Camera OCR',
      icon: Camera,
      color: 'green'
    },
    {
      id: 'speech',
      label: 'Text-to-Speech',
      icon: Volume2,
      color: 'orange'
    }
  ];

  const demoContent = {
    translation: {
      input: "Hello, how are you today?",
      output: "Bonjour, comment allez-vous aujourd'hui?",
      languages: { from: "English", to: "French" },
      description: "Experience instant translation with high accuracy"
    },
    voice: {
      input: "🎤 Speaking: 'Where is the nearest restaurant?'",
      output: "¿Dónde está el restaurante más cercano?",
      languages: { from: "English", to: "Spanish" },
      description: "Speak naturally and get instant translations"
    },
    camera: {
      input: "📸 Camera detected: 'EXIT'",
      output: "出口",
      languages: { from: "English", to: "Chinese" },
      description: "Point your camera at text for instant translation"
    },
    speech: {
      input: "Bonjour, comment allez-vous?",
      output: "🔊 Playing pronunciation...",
      languages: { from: "French", to: "Audio" },
      description: "Hear perfect pronunciation in any language"
    }
  };

  const handlePlayDemo = () => {
    setIsPlaying(true);
    setTimeout(() => setIsPlaying(false), 2000);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            See Quick Translator
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              In Action
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how our intelligent features work together to create the ultimate translation experience.
          </p>
        </motion.div>

        {/* Demo Selection */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {demoTypes.map((demo) => (
            <button
              key={demo.id}
              onClick={() => setActiveDemo(demo.id)}
              className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                activeDemo === demo.id
                  ? `bg-${demo.color}-600 text-white shadow-lg transform scale-105`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <demo.icon className="w-5 h-5 mr-2" />
              {demo.label}
            </button>
          ))}
        </motion.div>

        {/* Demo Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Phone Mockup */}
          <motion.div 
            className="relative max-w-sm mx-auto"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              <div className="w-80 h-160 bg-black rounded-3xl p-2 shadow-2xl">
                <div className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl overflow-hidden">
                  {/* Phone Status Bar */}
                  <div className="flex justify-between items-center p-4 text-xs text-gray-600">
                    <span>9:41</span>
                    <span>Quick Translator</span>
                    <span>100%</span>
                  </div>

                  {/* App Content */}
                  <div className="p-6">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeDemo}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-6"
                      >
                        {/* Language Selector */}
                        <div className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
                          <span className="text-sm font-medium text-gray-700">
                            {demoContent[activeDemo].languages.from}
                          </span>
                          <ArrowRightLeft className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            {demoContent[activeDemo].languages.to}
                          </span>
                        </div>

                        {/* Input */}
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <div className="text-sm text-gray-500 mb-2">Input</div>
                          <div className="text-gray-900">
                            {demoContent[activeDemo].input}
                          </div>
                        </div>

                        {/* Output */}
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                          <div className="text-sm text-blue-600 mb-2">Translation</div>
                          <div className="text-blue-900 font-medium">
                            {demoContent[activeDemo].output}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <button 
                            onClick={handlePlayDemo}
                            className="flex-1 bg-blue-600 text-white rounded-xl py-3 flex items-center justify-center"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            {isPlaying ? 'Playing...' : 'Play'}
                          </button>
                          <button className="bg-gray-200 text-gray-700 rounded-xl px-4 py-3">
                            <Volume2 className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Demo Description */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeDemo}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {demoTypes.find(d => d.id === activeDemo)?.label}
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  {demoContent[activeDemo].description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Feature Benefits */}
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-gray-900">Key Benefits:</h4>
              <ul className="space-y-3">
                {[
                  "Lightning-fast processing with Google ML Kit",
                  "High accuracy translations for real-world use",
                  "Intuitive interface designed for mobile",
                  "Seamless integration of all features"
                ].map((benefit, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="pt-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.suuin.quicktranslator"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Try Quick Translator Now
                <ArrowRightLeft className="w-5 h-5 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TranslatorDemo; 